"use client";

import { createContext, useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { MindtaleIntro } from "./MindtaleIntroLoading";
import { INTRO_SEEN_KEY, shouldSkipIntro } from "@/lib/intro-gate";

/** True once the intro finished (or was skipped) — pages use it to time
 *  their entrance animations to the curtain reveal. */
export const IntroDoneContext = createContext(false);

/** The server-rendered cream panel from app/layout.tsx. Removed as soon as
 *  we know the intro will not run (MindtaleIntro removes it itself once it
 *  has mounted on top of it, so there is never a gap). */
export function removeIntroCurtain() {
  document.getElementById("mindtale-curtain")?.remove();
}

// Module-level guard: the intro may start at most once per page load,
// no matter how many times the wrapper remounts (Clerk init, HMR, etc.).
let introStartedThisLoad = false;

export function LandingIntroWrapper({ children }: { children: React.ReactNode }) {
  const [showIntro, setShowIntro] = useState(false);
  const [decided, setDecided] = useState(false);
  const pathname = usePathname() ?? "";

  useEffect(() => {
    // Same condition as the inline pre-paint script in app/layout.tsx — both
    // read it from lib/intro-gate.ts, so the curtain and the intro always
    // agree. It skips anything but a direct first visit to the homepage:
    // an article reached from a search result shows its answer immediately.
    const timer = setTimeout(() => {
      if (shouldSkipIntro(pathname) || introStartedThisLoad) {
        removeIntroCurtain();
      } else {
        introStartedThisLoad = true;
        setShowIntro(true);
        sessionStorage.setItem(INTRO_SEEN_KEY, "true");
      }
      setDecided(true);
    }, 50);

    return () => clearTimeout(timer);
  }, [pathname]);

  return (
    <IntroDoneContext.Provider value={decided && !showIntro}>
      {showIntro && <MindtaleIntro onFinish={() => setShowIntro(false)} />}
      {children}
    </IntroDoneContext.Provider>
  );
}
